import { EntityReference } from '../basic';

export type ResultType = 'ResultError' | 'ResultInfo' | 'ResultSuccess' | 'ResultWarning';

/**
 * Interface for a result message to display as a box
 * for the box u should use the result-box component
 * @see libs/shared/src/template/app/components/result-box/result-box.component.ts
 */
export interface IResult {
  /** ID of the result */
  resultID?: number;
  /** Type of the result */
  resultType: ResultType;
  /** Text of the result */
  text: string;
  /** The stacktrace of the happend error */
  stacktrace?: unknown;
  /** The basket-item corresponding to this result-msg */
  basketItem?: EntityReference;
  /** The URL to redirect to, if clicked on the msg */
  url?: string;
}

export type BaseResultType = 'redirect' | 'ok' | 'validation' | 'error';

/**
 * Base interface for all "direct" results
 * This is used for results that are not always displayed directly,
 * but can be used to redirect the user or perform other actions.
 */
export interface IBaseResult {
  /**
   * Scripts the backend produced while the action ran (CS3 `PopJavaScripts()`).
   * Executed centrally by the cloudfront `trackingInterceptor`; do not evaluate
   * it in a component.
   */
  javaScript?: string;
  /** The type of the result */
  type: BaseResultType;
}

export interface IRedirectResult extends IBaseResult {
  /** The type of the result */
  type: 'redirect';
  /** The url to redirect to */
  url: string;
  /** Did the operation/task end successfull? */
  succeeded: boolean;
  /** The target for the redirect (could be undefined) */
  target?: '_self' | '_blank';
}

export interface IErrorResult extends IBaseResult {
  /** The type of the result */
  type: 'error';
  /** The error message */
  error: string;
  /** Did the operation/task end with an error? */
  exception?: boolean;
  /** Whether the error should be shown to the user or is just for logging purposes */
  public?: boolean;
  /** An optional error message to display to the user */
  message?: string;
  /** The stack trace of the error, if available */
  stackTrace?: string;
}

export interface IOkResult extends IBaseResult {
  /** The type of the result */
  type: 'ok';
  /** Did the operation/task end successfull? */
  succeeded: boolean;
  /** A message to display to the user */
  message: string;
}

/** Response of an form if there are any errors in any field */
export interface IUpdateVarErrorsResult extends IBaseResult {
  /** The type of the result */
  type: 'validation';
  /** Listing all the errors for the different fields returned by form-values inside the methods body */
  errors: IUpdateVarError[];
  /** The currently valid task of every rejected captcha field (CS4-2790) */
  captchas?: ICaptchaRefresh[];
}

/**
 * The task the backend actually checks a captcha against.
 * It changes when the backend session changes between loading and sending the form.
 */
export interface ICaptchaRefresh {
  /** The formCtrlName / attribute of the captcha field */
  componentName: string;
  /** The hint containing the task */
  hint?: string;
  /** The placeholder containing the task */
  placeHolder?: string;
  /**
   * The message to show instead of "the answer is wrong" when the task differs from the one
   * the form showed. The backend cannot tell, so it always sends it and the form decides.
   */
  text?: string;
}

/** The interface of the errors returned by form-values inside the methods body */
export interface IUpdateVarError {
  /** The formCtrlName / attribute of the corresponding field (null if overall error of the form) */
  componentName?: string;
  /** The text/error to show for this spicific field */
  text: string;
}

/** Typeguard to check if an object is of type IBaseResult */
export function isIBaseResult(obj: any): obj is IBaseResult {
  return obj && typeof obj.type === 'string' && ['redirect', 'ok', 'validation', 'error'].includes(obj.type);
}

/** Typeguard to check if an object is of type IRedirectResult */
export function isIRedirectResult(obj: any): obj is IRedirectResult {
  return isIBaseResult(obj) && obj.type === 'redirect' && 'url' in obj && typeof obj.url === 'string';
}

/** Typeguard to check if an object is of type IOkResult */
export function isIOkResult(obj: any): obj is IOkResult {
  return (
    isIBaseResult(obj) &&
    obj.type === 'ok' &&
    'succeeded' in obj &&
    typeof obj.succeeded === 'boolean' &&
    'message' in obj &&
    typeof obj.message === 'string'
  );
}

/** Typeguard to check if an object is of type IUpdateVarErrorsResult */
export function isIUpdateVarErrorsResult(obj: any): obj is IUpdateVarErrorsResult {
  return (
    isIBaseResult(obj) &&
    obj.type === 'validation' &&
    'errors' in obj &&
    Array.isArray(obj.errors) &&
    obj.errors.every((e: any) => (!e.componentName || typeof e.componentName === 'string') && typeof e.text === 'string')
  );
}
